"use client";

/**
 * SavedDiagnosticsList — renders a user's saved DiagnosticsSession records.
 * Supports: view results, continue in-progress, star, archive, delete, share.
 *
 * Uses DiagnosticsSession shape (matching React + Firestore) — NOT the old AssessmentRun.
 * Styling: design-system CSS classes from globals.css (.sdl-*, .btn, .btn-ghost, .btn-sm)
 * — no hardcoded colours or inline style objects.
 */
import { useState } from "react";
import Link from "next/link";
import { SenseiMarkLoader } from "@/shared/components/layout/SenseiMarkLoader";
import type { DiagnosticsSession } from "@/modes/diagnostics/types";
import { calculateMaturityLevel } from "@/modes/diagnostics/types";
import { ShareDialog, type DiagnosticCollaborator } from "./ShareDialog";

interface Props {
  sessions:        DiagnosticsSession[];
  loading:         boolean;
  error:           string | null;
  onDelete:        (id: string) => void;
  onToggleStar:    (id: string) => void;
  onToggleArchive: (id: string) => void;
  onRefresh:       () => void;
}

export function SavedDiagnosticsList({
  sessions, loading, error, onDelete, onToggleStar, onToggleArchive, onRefresh,
}: Props) {
  const [search,  setSearch]  = useState("");
  const [filter,  setFilter]  = useState<"all" | "starred" | "archived">("all");
  const [shareId, setShareId] = useState<string | null>(null);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="sdl-loading" style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px 0" }}>
        <SenseiMarkLoader size={40} />
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="sdl-error">
        <p>{error}</p>
        <button onClick={onRefresh} className="btn btn-ghost btn-sm">Retry</button>
      </div>
    );
  }

  /* ── Filter & search ── */
  const visible = sessions.filter((s) => {
    const q           = search.trim().toLowerCase();
    const matchSearch = !q || s.title.toLowerCase().includes(q) || s.diagnosticTitle.toLowerCase().includes(q);
    const matchFilter =
      filter === "all"     ? !s.isArchived :
      filter === "starred" ? s.isStarred && !s.isArchived :
      s.isArchived;
    return matchSearch && matchFilter;
  });

  const shareSession = sessions.find((s) => s.id === shareId);

  return (
    <>
      {/* ── Toolbar ── */}
      <div className="sdl-toolbar mt-4">
        <div className="sdl-search-wrap">
          {/* Search icon */}
          <svg className="sdl-search-icon" width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search saved diagnostics…"
            aria-label="Search saved diagnostics"
            className="sdl-search"
          />
        </div>

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          aria-label="Filter diagnostics"
          className="sdl-filter"
        >
          <option value="all">Active</option>
          <option value="starred">Starred</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* ── Empty state ── */}
      {visible.length === 0 ? (
        <div className="sdl-empty">
          {sessions.length === 0
            ? "No saved diagnostics yet. Run one to see it here."
            : "No diagnostics match your search."}
        </div>
      ) : (
        <div className="sdl-list">
          {visible.map((s) => {
            const maturity = s.isCompleted ? calculateMaturityLevel(s.score) : null;

            return (
              <div key={s.id} className="sdl-card">
                <div className="sdl-card-row">
                  {/* Content */}
                  <div className="sdl-card-body">
                    <h3 className="sdl-title">{s.title}</h3>
                    <div className="sdl-meta">
                      {s.isCompleted ? (
                        <>
                          <span className="sdl-pill sdl-pill--score">
                            Score: {s.score}
                          </span>
                          <span className={`sdl-pill sdl-pill--${maturity?.colorClass ?? "blue"}`}>
                            {maturity?.level}
                          </span>
                          <span>
                            Completed {new Date(s.completedAt).toLocaleDateString("en-GB", {
                              day: "numeric", month: "short", year: "numeric",
                            })}
                          </span>
                        </>
                      ) : (
                        <span>In progress — Q{(s.currentQuestion ?? 0) + 1} of {s.questions?.length ?? "?"}</span>
                      )}
                      {s.role === "collaborator" && (
                        <span className="sdl-meta-collab">Shared with you</span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="sdl-actions">
                    {/* Star */}
                    <button
                      onClick={() => onToggleStar(s.id)}
                      aria-label={s.isStarred ? "Unstar" : "Star"}
                      className={`sdl-icon-btn sdl-icon-btn--star${s.isStarred ? " is-starred" : ""}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill={s.isStarred ? "currentColor" : "none"}
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    </button>

                    {/* Archive */}
                    <button
                      onClick={() => onToggleArchive(s.id)}
                      aria-label={s.isArchived ? "Unarchive" : "Archive"}
                      className="sdl-icon-btn"
                    >
                      {s.isArchived ? (
                        /* Unarchive: folder-open icon */
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                        </svg>
                      ) : (
                        /* Archive: archive box icon */
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="21 8 21 21 3 21 3 8" />
                          <rect x="1" y="3" width="22" height="5" rx="1" />
                          <line x1="10" y1="12" x2="14" y2="12" />
                        </svg>
                      )}
                    </button>

                    {/* Share */}
                    <button
                      onClick={() => setShareId(s.id)}
                      aria-label="Share"
                      className="sdl-icon-btn"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                      </svg>
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => { if (confirm(`Delete "${s.title}"?`)) onDelete(s.id); }}
                      aria-label="Delete"
                      className="sdl-icon-btn sdl-icon-btn--delete"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* CTA */}
                <div className="sdl-cta-row">
                  {s.isCompleted ? (
                    <Link href={`/diagnostics/saved/${s.id}/results`} className="btn btn-ghost btn-sm">
                      View Results →
                    </Link>
                  ) : (
                    <Link href={`/diagnostics/saved/${s.id}/questions`} className="btn btn-ghost btn-sm">
                      Continue →
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Share dialog ── */}
      {shareId && shareSession && (
        <ShareDialog
          sessionId={shareId}
          sessionTitle={shareSession.title}
          sharedWith={shareSession.shared_with as DiagnosticCollaborator[]}
          existingShareToken={shareSession.share_token}
          onClose={() => setShareId(null)}
          onCollaboratorAdded={() => onRefresh()}
          onCollaboratorRemoved={() => onRefresh()}
          onShareTokenChanged={() => onRefresh()}
        />
      )}
    </>
  );
}
